import { COL_WIDTHS, FONT_SIZE } from "./constants";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import Button from "components/common/Button";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { useEffect, useState } from "react";

const RatingFactorGroup = ({
  factorKey,
  factorName,
  defaultValues,
  overrides,
  onUpdate,
  onDelete,
  isCustomGroup,
  pendingField,
  onPendingChange,
}) => {
  const allCategories = Array.from(
    new Set([
      ...Object.keys(defaultValues ?? {}),
      ...Object.keys(overrides ?? {}),
    ])
  );

  const showAddRow = pendingField?.show === true;

  const handleCommit = () => {
    const name = pendingField?.name?.trim();
    const value = pendingField?.value;
    if (!name || value == null || value <= 0) return;
    onUpdate(factorKey, name, value);
    onPendingChange(factorKey, { name: "", value: null, show: true });
  };

  const pendingName = pendingField?.name ?? "";
  const pendingValue = pendingField?.value ?? "";
  const pendingHasError =
    pendingValue !== "" &&
    (pendingField?.value == null || pendingField?.value <= 0);

  return (
    <Box sx={{ mb: 1 }}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        sx={{ px: 1, pt: 0.5, pb: 0.25 }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: isCustomGroup ? "primary.main" : "transparent",
            flexShrink: 0,
            mr: 0.75,
          }}
        />
        <Typography
          fontSize={"0.7rem"}
          fontWeight={700}
          color={isCustomGroup ? "primary.main" : "text.secondary"}
          sx={{ flex: 1 }}
        >
          {factorName}
        </Typography>
        {isCustomGroup && (
          <Tooltip title={"Delete rating factor"}>
            <IconButton size={"small"} onClick={onDelete} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 13, color: "text.disabled" }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Table size={"small"} sx={{ tableLayout: "fixed" }}>
        <colgroup>
          <col />
          <col style={{ width: COL_WIDTHS.default }} />
          <col style={{ width: COL_WIDTHS.override }} />
          <col style={{ width: COL_WIDTHS.action }} />
        </colgroup>
        <TableBody>
          {allCategories.map((category) => {
            const isCustom = !(defaultValues && category in defaultValues);
            const actualDefault = defaultValues?.[category];
            const override = overrides?.[category];
            const isModified = override != null;
            const displayValue = isModified ? override : actualDefault;
            const overrideHasError = override != null && override <= 0;
            const highlighted = isModified || isCustom;
            return (
              <TableRow
                key={category}
                sx={{
                  bgcolor: highlighted
                    ? "rgba(210,105,30,0.05)"
                    : "transparent",
                }}
              >
                <TableCell
                  sx={{
                    py: 0.5,
                    borderLeft: highlighted
                      ? "2px solid"
                      : "2px solid transparent",
                    borderColor: "primary.main",
                  }}
                >
                  <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
                    {highlighted && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography
                      fontSize={FONT_SIZE}
                      fontWeight={highlighted ? 600 : 400}
                      color={highlighted ? "primary.main" : "text.primary"}
                    >
                      {category}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align={"right"} sx={{ py: 0.5 }}>
                  <Typography
                    fontSize={FONT_SIZE}
                    color={"text.secondary"}
                    sx={{
                      textDecoration:
                        isModified && !isCustom ? "line-through" : "none",
                    }}
                  >
                    {isCustom ? "—" : numbers.dpString(3)(actualDefault)}
                  </Typography>
                </TableCell>
                <TableCell align={"right"} sx={{ py: 0.5 }}>
                  <Tooltip
                    title={overrideHasError ? "Value must be > 0" : ""}
                    open={overrideHasError}
                  >
                    <PrettyNumberTextField
                      size={"small"}
                      hiddenLabel
                      value={displayValue ?? ""}
                      placeholder={
                        !isCustom && actualDefault != null
                          ? numbers.dpString(3)(actualDefault)
                          : ""
                      }
                      onChangeNumber={(v) =>
                        onUpdate(factorKey, category, v ?? null)
                      }
                      error={overrideHasError}
                      variant={"filled"}
                      inputProps={{
                        style: { textAlign: "right", fontSize: FONT_SIZE },
                      }}
                      sx={{ width: "100%" }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align={"center"} sx={{ py: 0.5, px: 0 }}>
                  {highlighted && (
                    <Tooltip
                      title={isCustom ? "Remove field" : "Clear override"}
                    >
                      <IconButton
                        size={"small"}
                        onClick={() => onUpdate(factorKey, category, null)}
                        sx={{ p: 0.25 }}
                      >
                        <CloseIcon
                          sx={{ fontSize: 13, color: "text.disabled" }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}

          {showAddRow && (
            <TableRow>
              <TableCell sx={{ py: 0.5, borderLeft: "2px solid transparent" }}>
                <TextField
                  size={"small"}
                  variant={"filled"}
                  hiddenLabel
                  autoFocus
                  placeholder={"Field Name"}
                  value={pendingName}
                  onChange={(e) =>
                    onPendingChange(factorKey, {
                      ...pendingField,
                      name: e.target.value,
                    })
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleCommit()}
                  inputProps={{
                    style: { fontSize: FONT_SIZE, padding: "4px 8px" },
                  }}
                  sx={{ width: "100%" }}
                />
              </TableCell>
              <TableCell />
              <TableCell align={"right"} sx={{ py: 0.5 }}>
                <Tooltip
                  title={pendingHasError ? "Value must be > 0" : ""}
                  open={pendingHasError}
                >
                  <PrettyNumberTextField
                    size={"small"}
                    hiddenLabel
                    value={pendingValue}
                    onChangeNumber={(v) =>
                      onPendingChange(factorKey, { ...pendingField, value: v })
                    }
                    onBlur={handleCommit}
                    error={pendingHasError}
                    variant={"filled"}
                    inputProps={{
                      style: { textAlign: "right", fontSize: FONT_SIZE },
                    }}
                    sx={{ width: "100%" }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell />
            </TableRow>
          )}

          <TableRow sx={{ "& td": { border: 0 } }}>
            <TableCell
              colSpan={4}
              sx={{ py: 0.25, pl: 1, borderLeft: "2px solid transparent" }}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                spacing={0.5}
                onClick={() =>
                  onPendingChange(factorKey, {
                    name: "",
                    value: null,
                    show: !showAddRow,
                  })
                }
                sx={{
                  cursor: "pointer",
                  display: "inline-flex",
                  color: "text.disabled",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <AddIcon sx={{ fontSize: 13 }} />
                <Typography fontSize={"0.65rem"}>{"New Field"}</Typography>
              </Stack>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const FactorTableModal = ({
  open,
  onClose,
  onApply,
  selectFlags,
  config,
  draft,
  onDraftChange,
  customFactorKeys,
  onCustomFactorKeysChange,
}) => {
  const [newFactorKey, setNewFactorKey] = useState("");
  const [showNewFactor, setShowNewFactor] = useState(false);
  const [pendingFields, setPendingFields] = useState({});

  useEffect(() => {
    if (open) {
      setPendingFields({});
      setShowNewFactor(false);
      setNewFactorKey("");
    }
  }, [open]);

  const handlePendingChange = (factorKey, pending) => {
    setPendingFields((prev) => ({ ...prev, [factorKey]: pending }));
  };

  const handleUpdate = (factorKey, category, value) => {
    const current = { ...(draft[factorKey] ?? {}) };
    if (value == null) {
      delete current[category];
    } else {
      current[category] = value;
    }
    const next = { ...draft, [factorKey]: current };
    if (Object.keys(current).length === 0) delete next[factorKey];
    onDraftChange(next);
  };

  const handleAddFactor = () => {
    const key = newFactorKey.trim();
    if (
      !key ||
      selectFlags.some((f) => f.type === key) ||
      customFactorKeys.includes(key)
    )
      return;
    onCustomFactorKeysChange([...customFactorKeys, key]);
    setNewFactorKey("");
    setShowNewFactor(false);
  };

  const handleDeleteCustomFactor = (key) => {
    onCustomFactorKeysChange(customFactorKeys.filter((k) => k !== key));
    const next = { ...draft };
    delete next[key];
    onDraftChange(next);
  };

  const handleApply = () => {
    let finalDraft = { ...draft };
    for (const [factorKey, pending] of Object.entries(pendingFields)) {
      const name = pending?.name?.trim();
      if (name && pending?.value != null && pending.value > 0) {
        finalDraft = {
          ...finalDraft,
          [factorKey]: {
            ...(finalDraft[factorKey] ?? {}),
            [name]: pending.value,
          },
        };
      }
    }
    onApply(finalDraft);
  };

  const allFactorGroups = [
    ...selectFlags.map((f) => ({
      key: f.type,
      name: f.displayName ?? f.type,
      defaultValues: config?.ratingFactors?.[f.type],
      isCustomGroup: false,
    })),
    ...customFactorKeys.map((k) => ({
      key: k,
      name: k,
      defaultValues: undefined,
      isCustomGroup: true,
    })),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"sm"}
      fullWidth
      scroll={"paper"}
    >
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700, pb: 1 }}>
        {"Edit Factor Values"}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            sx={{ pl: 1, pr: `${COL_WIDTHS.action}px`, pb: 0.5 }}
          >
            <Typography
              fontSize={"0.8rem"}
              fontWeight={600}
              color={"text.secondary"}
              sx={{ flex: 1 }}
            >
              {"Category"}
            </Typography>
            <Typography
              fontSize={"0.8rem"}
              fontWeight={600}
              color={"text.secondary"}
              sx={{ width: COL_WIDTHS.default, textAlign: "right" }}
            >
              {"Default"}
            </Typography>
            <Typography
              fontSize={"0.8rem"}
              fontWeight={600}
              color={"primary.main"}
              sx={{ width: COL_WIDTHS.override, textAlign: "right", pr: 1 }}
            >
              {"Override"}
            </Typography>
          </Stack>
          {allFactorGroups.map(
            ({ key, name, defaultValues, isCustomGroup }) => (
              <RatingFactorGroup
                key={key}
                factorKey={key}
                factorName={name}
                defaultValues={defaultValues}
                overrides={draft[key]}
                onUpdate={handleUpdate}
                onDelete={
                  isCustomGroup
                    ? () => handleDeleteCustomFactor(key)
                    : undefined
                }
                isCustomGroup={isCustomGroup}
                pendingField={pendingFields[key]}
                onPendingChange={handlePendingChange}
              />
            )
          )}
        </Box>

        <Divider />
        <Box sx={{ px: 3, py: 1 }}>
          {showNewFactor ? (
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <TextField
                size={"small"}
                variant={"outlined"}
                autoFocus
                placeholder={"Rating Factor Name"}
                value={newFactorKey}
                onChange={(e) => setNewFactorKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddFactor()}
                inputProps={{ style: { fontSize: FONT_SIZE } }}
                sx={{ flex: 1 }}
              />
              <Tooltip title={"Add"}>
                <span>
                  <IconButton
                    size={"small"}
                    onClick={handleAddFactor}
                    disabled={!newFactorKey.trim()}
                    color={"primary"}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={"Cancel"}>
                <IconButton
                  size={"small"}
                  onClick={() => {
                    setShowNewFactor(false);
                    setNewFactorKey("");
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={0.5}
              onClick={() => setShowNewFactor(true)}
              sx={{
                cursor: "pointer",
                display: "inline-flex",
                color: "text.disabled",
                "&:hover": { color: "primary.main" },
              }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
              <Typography fontSize={"0.7rem"} fontWeight={600}>
                {"Add New Rating Factor"}
              </Typography>
            </Stack>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          color={"secondary"}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {"Cancel"}
        </Button>
        <Button
          onClick={handleApply}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {"Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FactorTableModal;
